import { useState, useEffect } from 'react';
import { createCertification, updateCertification } from '../../services/content/certificationService';
import { searchCourseGroups } from '../../services/content/courseGroupService';
import type { ContentCertification } from '../../types/content/certification';
import type { CourseGroup } from '../../types/content/courseGroup';

interface CertificationModalProps {
  certification?: ContentCertification;
  onClose: () => void;
  onSuccess: () => void;
}

export function CertificationModal({ certification, onClose, onSuccess }: CertificationModalProps) {
  const isEdit = !!certification;

  const [name, setName] = useState(certification?.name || '');
  const [issuer, setIssuer] = useState(certification?.issuer || '');
  const [courseGroupId, setCourseGroupId] = useState(certification?.courseGroupId || '');
  const [defaultOrder, setDefaultOrder] = useState<string>(
    certification?.defaultOrder != null ? String(certification.defaultOrder) : '0'
  );
  const [courseGroups, setCourseGroups] = useState<CourseGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    searchCourseGroups({}, { pageSize: 200, orderBy: 'name', order: 'asc' })
      .then(setCourseGroups)
      .catch(() => {});
  }, []);

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  async function handleSave() {
    if (!name.trim() || !courseGroupId.trim()) return;
    setError('');
    setLoading(true);
    try {
      const data = {
        name: name.trim(),
        issuer: issuer.trim(),
        courseGroupId: courseGroupId.trim(),
        defaultOrder: defaultOrder.trim() ? parseInt(defaultOrder, 10) : 0,
      };
      if (isEdit) {
        await updateCertification(certification!.id, { ...certification!, ...data });
      } else {
        await createCertification(data);
      }
      onSuccess();
    } catch {
      setError(isEdit ? 'Failed to update certification' : 'Failed to create certification');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {isEdit ? 'Edit Certification' : 'Add Certification'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Certification name" />
            </div>
            <div>
              <label className={labelClass}>Issuer</label>
              <input type="text" value={issuer} onChange={(e) => setIssuer(e.target.value)} className={inputClass} placeholder="Issuing organization" />
            </div>
            <div>
              <label className={labelClass}>Course Group *</label>
              <select value={courseGroupId} onChange={(e) => setCourseGroupId(e.target.value)} className={inputClass}>
                <option value="">Select...</option>
                {courseGroups.map((cg) => (
                  <option key={cg.id} value={cg.id}>{cg.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Default Order</label>
              <input
                type="number"
                value={defaultOrder}
                onChange={(e) => setDefaultOrder(e.target.value)}
                className={inputClass}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">Global display order — used when a Client hasn't set its own override.</p>
            </div>
          </div>

          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={loading || !name.trim() || !courseGroupId.trim()} className={btnPrimary}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
